import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import axios, { CancelToken } from "axios";

import "./EmergencyContactCard.scss";
import Input from "components/form/input/Input";
import Card from "components/card/Card";
import Form from "components/form/Form";
import FormLayout from "components/form/FormLayout";
import { ButtonNew as Button } from "components/buttonNew";
import Divider from "components/divider";
import { Select } from "components/select/index";

import { StatusCode } from "api/protocols";
import {
  DeleteCustomerEmergencyContact,
  GetCustomer,
  GetCustomerEmergencyContacts,
  IEmergencyContact,
  PostUserEmergencyContact,
} from "api/rpc/2024-04/facilityAdmin/customer/customer";

import { dequeue, enqueue, showError, showSuccess } from "redux/actions/ui";
import { ICustomer } from "redux/reducers/models/customer";
import { useCustomerContext } from "../../../context/CustomerContext";

interface IFilterState {
  search: string;
  searching: boolean;
}

interface IEmergencyContactExtension {
  isEditing?: boolean;
  newContact?: boolean;
}

const defaultEmergencyContact: IEmergencyContact & IEmergencyContactExtension = {
  first_name: "",
  last_name: "",
  relation: "",
  email: "",
  phone: "",
  id: null,
  isEditing: false,
  newContact: false,
  profile_image_source: "",
};

interface IEmergencyContactProps {
  onRefresh?: () => Promise<void>;
}
export default function EmergencyContactCard(props: IEmergencyContactProps) {
  const { t } = useTranslation();
  const { onRefresh } = props;
  const dispatch = useDispatch();
  const { Option } = Select;
  const { state } = useCustomerContext();
  const [emergencyContacts, setEmergencyContacts] = useState<(IEmergencyContact & IEmergencyContactExtension)[]>([
    defaultEmergencyContact,
    defaultEmergencyContact,
  ]);

  const [emergencyContactsBefore, setEmergencyContactsBefore] = useState<
    (IEmergencyContact & IEmergencyContactExtension)[]
  >([defaultEmergencyContact, defaultEmergencyContact]);

  const [customers, setCustomers] = useState<ICustomer[]>([]);

  const [filter, setFilter] = useState<IFilterState>({
    search: "",
    searching: false,
  });

  useEffect(() => {
    void loadEmergencyContacts();
  }, [state.customer.emergency_contact_id_1, state?.customer.emergency_contact_id_2]);

  useEffect(() => {
    const source = axios.CancelToken.source();

    void loadCustomers(source.token);

    return () => {
      source.cancel();
    };
  }, [filter.search]);

  async function loadCustomers(token?: CancelToken) {
    const customerRes = await GetCustomer({ search: filter.search }, false, token);
    setFilter(prev => ({ ...prev, searching: false }));

    if (token && token.reason) {
      return;
    }
    if (customerRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error loading customers")));
      return;
    }

    setCustomers(customerRes.status !== StatusCode.OK ? [] : customerRes.data);
  }

  async function loadEmergencyContacts() {
    const params = {
      emergency_contact_ids: [
        state?.customer?.emergency_contact_id_1 ?? null,
        state?.customer?.emergency_contact_id_2 ?? null,
      ],
      customer_id: state?.customer?.id,
    };

    const emergencyContactRes = await GetCustomerEmergencyContacts(params, true);
    if (emergencyContactRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error occurred loading emergency contacts")));
    }

    setEmergencyContacts(emergencyContactRes.data as IEmergencyContact[]);
    setEmergencyContactsBefore(emergencyContactRes.data as IEmergencyContact[]);
    void clearFilterSearch();
  }

  async function removeContact(contact: IEmergencyContact) {
    const deletedContactsRes = await DeleteCustomerEmergencyContact(
      { emergency_contact_id: contact.id, user_id: state.customer.id },
      true,
    );

    if (deletedContactsRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error occurred removing emergency contact")));
      return;
    }

    dispatch(showSuccess(t("Emergency contact successfully removed")));
    return;
  }

  async function handleRemoveContact(contact: IEmergencyContact, index: number) {
    if (emergencyContacts[index].newContact) {
      const updatedContacts = [...emergencyContacts];
      updatedContacts.splice(index, 1);
      setEmergencyContacts(updatedContacts);
    } else if (
      contact.id === state.customer?.emergency_contact_id_1 ||
      (contact.id === state.customer?.emergency_contact_id_2 && contact.id !== null)
    ) {
      await removeContact(contact);
    } else {
      const updatedContacts = [...emergencyContacts];
      updatedContacts.splice(index, 1);
      setEmergencyContacts(updatedContacts);
    }

    void clearFilterSearch();
    await onRefresh();
    return;
  }

  function handleInputChange(e: React.ChangeEvent<HTMLInputElement>, index: number): void {
    const { id, value } = e.target;

    if (id === "phone" && value.length > 10) {
      dispatch(showError(t("Phone number cannot exceed 10 digits")));
      return;
    }

    const updatedContacts = [...emergencyContacts];

    updatedContacts[index] = {
      ...updatedContacts[index],
      [id]: value,
    };

    if (contactContentsAreEmpty(updatedContacts[index])) {
      updatedContacts[index] = null;
    }

    setEmergencyContacts(updatedContacts);
    return;
  }

  function contactContentsAreEmpty(contactInputs: IEmergencyContact) {
    return Object.values(contactInputs).every(value => value === "");
  }
  async function saveEmergencyContacts() {
    const saveContact = emergencyContacts.filter(
      emergencyContact => !emergencyContactsBefore.includes(emergencyContact),
    );

    dispatch(enqueue());
    for (const e of saveContact) {
      const res = await PostUserEmergencyContact({ customer_id: state?.customer?.id, contact: e }, false);
      if (res.status !== StatusCode.OK) {
        dispatch(dequeue());
        dispatch(showError(t("Error occurred saving emergency contacts")));
        return;
      }
    }
    dispatch(dequeue());
    dispatch(showSuccess(t("Emergency contacts saved successfully")));
    void clearFilterSearch();
    await onRefresh();
  }

  function handleCustomerSelect(customer_id: number, customer: ICustomer) {
    let contact = undefined;
    if (customer_id === null) {
      contact = {
        first_name: "",
        last_name: "",
        phone: "",
        email: "",
        relation: "",
        id: null,
        isEditing: false,
        profile_image_source: "",
      };
    } else {
      contact = {
        first_name: customer.first_name,
        last_name: customer.last_name,
        phone: customer.phone,
        email: customer.email,
        relation: "",
        id: customer_id,
        isEditing: false,
        newContact: true,
        profile_image_source: customer.profile_image_source,
      };
    }

    const updatedContacts = [...emergencyContacts];
    if (updatedContacts.length === 0 || updatedContacts[0] === undefined || updatedContacts[0] === null) {
      updatedContacts[0] = contact;
    } else if (updatedContacts.length === 1 || updatedContacts[1] === undefined || updatedContacts[1] === null) {
      updatedContacts[1] = contact;
    }

    setEmergencyContacts(updatedContacts);
    void clearFilterSearch();
  }

  function handleCancelEmergencyContacts() {
    setEmergencyContacts(emergencyContactsBefore);
    void clearFilterSearch();
  }

  function clearFilterSearch() {
    setFilter(prev => ({ ...prev, search: "" }));
  }
  function handleShowEditInputs(index: number) {
    const updatedContacts = [...emergencyContacts];
    updatedContacts[index] = {
      ...updatedContacts[index],
      isEditing: !updatedContacts[index].isEditing,
    };
    setEmergencyContacts(updatedContacts);
  }

  // Components
  const ContactInputCard = (contact: IEmergencyContact, contactIndex: number) => {
    return (
      <Form>
        <Divider />
        <FormLayout>
          <FormLayout.Group>
            <Input
              id={"first_name"}
              value={contact?.first_name ?? ""}
              disabled={contact?.id}
              label={t("First Name")}
              placeholder={t("First Name")}
              onChange={e => handleInputChange(e, contactIndex)}
            />
            <Input
              id={"last_name"}
              value={contact?.last_name ?? ""}
              disabled={contact?.id}
              label={t("Last Name")}
              placeholder={t("Last Name")}
              onChange={e => handleInputChange(e, contactIndex)}
            />
          </FormLayout.Group>

          <FormLayout.Group>
            <Input
              id={"relation"}
              value={contact?.relation ?? ""}
              label={t("Relation")}
              placeholder={t("Relation")}
              onChange={e => handleInputChange(e, contactIndex)}
            />
            <Input
              id={"phone"}
              value={contact?.phone ?? ""}
              disabled={contact?.id}
              label={t("Phone")}
              placeholder={"123-456-7890"}
              type={"number"}
              onChange={e => handleInputChange(e, contactIndex)}
            />
          </FormLayout.Group>

          <FormLayout.Group>
            <Input
              id={"email"}
              value={contact?.email ?? ""}
              disabled={contact?.id}
              label={t("Email")}
              placeholder={"example@email.com"}
              onChange={e => handleInputChange(e, contactIndex)}
            />
          </FormLayout.Group>
        </FormLayout>
        <Divider />
      </Form>
    );
  };

  const ContactCard = (contact: IEmergencyContact, index?: number) => {
    return (
      <div className="contact-card pb-2">
        <div className="flex flex-row">
          <div className="w-16">
            <img
              src={
                contact?.profile_image_source !== null
                  ? contact?.profile_image_source
                  : "/public/images/default_profile_image.svg"
              }
              alt={t('customer_image')}
            />
          </div>
          <div className="flex flex-col pl-2 pt-1">
            <div className="flex flex-row">
              <p>
                {contact.first_name} {contact.last_name}
              </p>
            </div>

            <div className="contact-card-details">
              <p>
                {contact.relation} • {contact.phone ? contact.phone : null}
              </p>
            </div>
          </div>
        </div>

        <div className="contact-card-buttons">
          <p className="contact-card-buttons-edit" onClick={() => handleShowEditInputs(index)}>
            {t('Edit')}
          </p>
          <p className="contact-card-buttons-warning" onClick={() => void handleRemoveContact(contact, index)}>
            {t('Remove')}
          </p>
        </div>
      </div>
    );
  };

  const newCustomer: ICustomer = null;
  // Page
  return (
    <>
      <Card title={t("Emergency Contacts")} collapsable>
        <div className="pt-4 pl-4 pr-4">
          <p>{t('Search or add an emergency contact')}</p>
          <Select
            onSearch={(query: string) => setFilter(prev => ({ ...prev, search: query, searching: true }))}
            onChange={(id: number, customer: ICustomer) => handleCustomerSelect(id, customer)}
            searchValue={filter.search}
            loading={filter.searching}
            showDropDownOnFocus={true}
            placeholder={""}
            allowClear
            customerSearch
            showSearch
            disabled={emergencyContacts?.length === 2 ? true : false}
          >
            {customers.length === 0 ? (
              <div className="ui-select-dropdown-list-item" onClick={() => handleCustomerSelect(null, newCustomer)}>
                <p className="text-md">{t('Add New Customer')}</p>
              </div>
            ) : null}
            {customers
              ?.filter(
                customer =>
                  customer.id !== state?.customer?.id &&
                  customer.id !== emergencyContacts[0]?.id &&
                  customer.id !== emergencyContacts[1]?.id,
              )
              ?.map((customer, index) => {
                return (
                  <Option key={index} value={customer.id} name={customer.full_name} extraValues={customer}>
                    <div className="flex justify-between">
                      <div>
                        <div className="text-semibold text-lg">{customer?.full_name}</div>
                        <div className="text-sm text-gray-500">{customer.customer_type}</div>
                        <div className="text-sm text-gray-500">{customer.email}</div>
                        <div className="text-sm text-gray-500">{customer.phone ? customer.phone : null}</div>
                      </div>

                      <div className="font-medium text-base text-gray-500 self-end">{customer.member_code}</div>
                    </div>
                  </Option>
                );
              })}
          </Select>
          <Divider />
        </div>

        {emergencyContacts?.map((contact, contactIndex) => {
          return (
            <div key={contactIndex} className="pt-2 pl-4 pr-4">
              {contact ? (
                <>
                  {contact?.id !== null ? ContactCard(contact, contactIndex) : null}
                  {contact?.isEditing || contact?.id === null ? ContactInputCard(contact, contactIndex) : null}
                </>
              ) : null}
            </div>
          );
        })}

        <div className="add-additonal-contact-button pr-4 pb-4 pt-4">
          <Button onClick={() => void handleCancelEmergencyContacts()} className="remove-contact-button">
            {t('Cancel')}
          </Button>
          <Button type="primary" onClick={() => void saveEmergencyContacts()}>
            {t('Save')}
          </Button>
        </div>
      </Card>
    </>
  );
}
